import { clampColumnCount } from '@/utils/column_layout';

export interface YurtzeitenDesignTemplateSettings_N {
  // The template's design, authored in the TipTap editor. Each record's block
  // is a <div data-yz-layout="entry">; the renderer fills the Nth block from
  // the Nth record, swapping {englishName} / {hebrewName} / … for its values.
  html: string;
  // The card the design is drawn on — the box sitting behind the photo and the
  // names. A hex colour, or 'transparent' so the design sits straight on
  // whatever is behind it.
  borderColor: string;
  backgroundColor: string;
  // Number of cards shown across one row of the yurtzeiten layout: 1–3 preset
  // or 4–12 custom (see utils/column_layout).
  columns: number;
  // Number of rows of cards on one screenful, same 1–3 preset / 4–12 custom
  // range as `columns`. A page holds rows × columns cards.
  rows: number;
  // Padding inside each rendered yurtzeiten card, stored as "verticalpx horizontalpx".
  cardPadding: string;
  // Space between rendered yurtzeiten cards, in pixels.
  spacing: number;
  // Width of the authored content inside the card, measured by the editor.
  // Designs saved before this field existed use their intrinsic width.
  contentWidth?: number;
}

export interface YurtzeitenDesignTemplate_N {
  id: string;
  label: string;
  description: string;
  // How many entry blocks the layout ships with. The preview fills them from
  // real records and pads the rest, so the design always reads the way it will
  // on a full screen.
  entryCount: number;
  // Only the markup differs between templates — the card colours all start
  // from the shared defaults below.
  defaults: Pick<YurtzeitenDesignTemplateSettings_N, 'html'>;
}

// Every template renders onto the same canvas, so a card thumbnail is a true
// miniature of the live preview — the renderer only ever scales this box.
//
// `width` is fixed: it is what makes line breaks land identically in the editor
// and the preview. `height` is only the nominal shape a design is drawn
// against — neither surface is pinned to it. Both size to their content and
// share the floor below, so the same design is exactly as tall in the editor as
// it is in the preview.
export const YURTZEITEN_TEMPLATE_CANVAS = { width: 560, height: 380 };

// The smallest a canvas may get: enough that an emptied design still has
// somewhere to click, small enough that a real one never inherits blank space.
// The rendered card adds its current vertical padding around this content area.
export const YURTZEITEN_CANVAS_MIN_CONTENT_HEIGHT = 120;

/* --------------------------------- colours ---------------------------------
 * The card behind the design. The defaults are the neutral sheet the editor
 * and the preview drew before the colours were configurable, so an existing
 * design looks exactly the same after this shipped.
 */
export const YURTZEITEN_CARD_BORDER_COLOR_DEFAULT_N = '#E6E8EE';
export const YURTZEITEN_CARD_BACKGROUND_COLOR_DEFAULT_N = '#FFFFFF';
export const YURTZEITEN_CARD_PADDING_DEFAULT_VERTICAL_N = 12;
export const YURTZEITEN_CARD_PADDING_DEFAULT_HORIZONTAL_N = 14;
export const YURTZEITEN_CARD_PADDING_DEFAULT_N =
  `${YURTZEITEN_CARD_PADDING_DEFAULT_VERTICAL_N}px ${YURTZEITEN_CARD_PADDING_DEFAULT_HORIZONTAL_N}px`;
export const YURTZEITEN_CARD_PADDING_MIN_N = 0;
export const YURTZEITEN_CARD_PADDING_MAX_N = 200;
export const YURTZEITEN_CARD_SPACING_DEFAULT_N = 12;
export const YURTZEITEN_CARD_SPACING_MIN_N = 0;
export const YURTZEITEN_CARD_SPACING_MAX_N = 120;
// The editor caps a custom design at the original 560px card width, but the
// rendered card itself is allowed to hug its content and grow only by padding.
export const YURTZEITEN_TEMPLATE_CONTENT_WIDTH =
  YURTZEITEN_TEMPLATE_CANVAS.width - 28 * 2;
export const YURTZEITEN_CANVAS_MIN_HEIGHT =
  YURTZEITEN_CANVAS_MIN_CONTENT_HEIGHT + YURTZEITEN_CARD_PADDING_DEFAULT_VERTICAL_N * 2;

// The keyword the background field accepts alongside a hex value.
export const YURTZEITEN_TRANSPARENT_COLOR_N = 'transparent';

// A whole number 1–12 (presets 1–3, Custom 4–12); missing or unreadable → 1.
export const normalizeYurtzeitenCardColumns_N = (value: unknown): number =>
  clampColumnCount(value);

// Same range as the columns; missing (every design saved before rows existed)
// or unreadable → 1, the single row those designs were always drawn with.
export const normalizeYurtzeitenCardRows_N = (value: unknown): number =>
  clampColumnCount(value);

export const getYurtzeitenCardPaddingParts_N = (
  value: unknown = YURTZEITEN_CARD_PADDING_DEFAULT_N
): { vertical: number; horizontal: number } => {
  const fallback = {
    vertical: YURTZEITEN_CARD_PADDING_DEFAULT_VERTICAL_N,
    horizontal: YURTZEITEN_CARD_PADDING_DEFAULT_HORIZONTAL_N,
  };
  const raw = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!raw) return fallback;

  const tokens = raw.split(' ');
  if (tokens.length > 4) return fallback;

  const normalizedTokens = tokens.map((token) => {
    const match = token.match(/^(\d+(?:\.\d+)?)(px)?$/i);
    if (!match) return null;
    const numericValue = Math.min(
      Math.max(Number(match[1]), YURTZEITEN_CARD_PADDING_MIN_N),
      YURTZEITEN_CARD_PADDING_MAX_N
    );
    return Number.isInteger(numericValue)
      ? numericValue
      : Number(numericValue.toFixed(2));
  });

  if (!normalizedTokens.every((token) => token !== null)) return fallback;

  return {
    vertical: normalizedTokens[0] as number,
    horizontal: (normalizedTokens[1] ?? normalizedTokens[0]) as number,
  };
};

export const normalizeYurtzeitenCardPadding_N = (value: unknown): string => {
  const parts = getYurtzeitenCardPaddingParts_N(value);
  return `${parts.vertical}px ${parts.horizontal}px`;
};

export const normalizeYurtzeitenCardSpacing_N = (value: unknown): number => {
  const spacing = Number(value);
  if (!Number.isFinite(spacing)) return YURTZEITEN_CARD_SPACING_DEFAULT_N;
  return Math.min(
    Math.max(Math.round(spacing), YURTZEITEN_CARD_SPACING_MIN_N),
    YURTZEITEN_CARD_SPACING_MAX_N
  );
};

// Invalid legacy/input values are ignored rather than allowed to collapse the
// card. The editor cannot author content wider than its content area.
export const normalizeYurtzeitenContentWidth_N = (value: unknown): number | null => {
  const width = Number(value);
  if (!Number.isFinite(width) || width <= 0) return null;
  return Math.min(Math.max(Math.round(width), 1), YURTZEITEN_TEMPLATE_CONTENT_WIDTH);
};

/**
 * Collapse records that describe the SAME person to the first of them.
 *
 * A yahrzeit can reach the board more than once as separate rows — the public
 * form and the Add modal both create submissions, and the same family often
 * sends one twice. The review queue is right to keep both (they are two
 * submissions, and each was approved on its own), but the board is not: a
 * memorial row showing one name twice reads as a fault, and with two or three
 * columns it fills the row with a single person while others wait for their
 * turn.
 *
 * Identity is every field a viewer can see them by — the English name, the
 * Hebrew name, and both dates. Two rows agreeing on all of that are the same
 * person by any reading a screen can make; anything less is left alone, so two
 * genuinely different people are never merged.
 */
export const dedupeYurtzeitenDisplayRecords_N = <T extends Record<string, any>>(
  records: T[]
): T[] => {
  const list = Array.isArray(records) ? records.filter(Boolean) : [];
  const seen = new Set<string>();
  const unique: T[] = [];

  const part = (value: unknown): string => {
    if (value instanceof Date) return String(value.getTime());
    return String(value ?? '').trim().toLowerCase();
  };

  list.forEach((record) => {
    const identity = [
      part(record.name),
      part(record.hebrewName),
      part(record.yahrzeitDate),
      part(record.hebrewDate),
    ].join('|');

    if (seen.has(identity)) return;
    seen.add(identity);
    unique.push(record);
  });

  return unique;
};

/**
 * How many cards stand side by side on the board — and, read with `rows`, how
 * many rows of them one screenful holds.
 *
 * The count is stored per template — `settings['stacked'].columns`,
 * `settings['two-column'].columns` — while which template is live is a separate
 * field. A design whose ACTIVE template carries no count of its own therefore
 * fell back to a single column, however many the other template had been given:
 * a one-card row, which is not what the design was set to.
 *
 * So the active template's own value wins whenever it has one, and otherwise the
 * widest count the design carries anywhere is used before falling back to one.
 * A board is only run at a single column when that is genuinely all the design
 * asks for.
 */
const resolveYurtzeitenDisplayCount_N = (
  design: { templateId?: string | null; settings?: Record<string, any> | null } | null | undefined,
  field: 'columns' | 'rows'
): number => {
  const settings = design?.settings;
  const source = settings && typeof settings === 'object' && !Array.isArray(settings)
    ? settings
    : {};

  const templateId = findYurtzeitenTemplate_N(
    design?.templateId || DEFAULT_YURTZEITEN_TEMPLATE_ID_N
  ).id;

  const isStored = (value: unknown): boolean =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const active = source[templateId];
  if (active && isStored(active[field])) {
    return clampColumnCount(active[field]);
  }

  return YURTZEITEN_DESIGN_TEMPLATES_N.reduce<number>((widest, template) => {
    const stored = source[template.id];
    if (!stored || !isStored(stored[field])) return widest;
    const count = clampColumnCount(stored[field]);
    return count > widest ? count : widest;
  }, 1);
};

export const resolveYurtzeitenDisplayColumns_N = (
  design: { templateId?: string | null; settings?: Record<string, any> | null } | null | undefined
): number => resolveYurtzeitenDisplayCount_N(design, 'columns');

// How many rows of cards one screenful holds, resolved exactly as the columns
// are. A design saved before rows existed carries none anywhere and stays at
// one row.
export const resolveYurtzeitenDisplayRows_N = (
  design: { templateId?: string | null; settings?: Record<string, any> | null } | null | undefined
): number => resolveYurtzeitenDisplayCount_N(design, 'rows');

// One screenful of the live display: the rows of cards the artboard shows
// before it rotates on — `rows` × `columns` cards. Splitting the records into
// pages is what lets a board with more yahrzeits than one screenful cycle
// through every one of them instead of showing the first page and dropping the
// rest.
//
// A page is a plain slice of the list, so no two cards on screen ever hold the
// same yahrzeit: with two or three columns each column shows a different person.
// A last page holding fewer records than the row has room for is left short
// rather than padded from the top of the list — `buildYurtzeitenDisplayCardSizes_N`
// spreads what it has across the row and the renderer centres it, so a single
// eligible yahrzeit is one centred card instead of the same name repeated in
// every column.
export const buildYurtzeitenDisplayPages_N = <T>(
  records: T[],
  entriesPerCard: number,
  columns: number,
  rows = 1
): T[][] => {
  const list = Array.isArray(records) ? records.filter(Boolean) : [];
  if (!list.length) return [];

  const perCard = Math.max(1, Math.floor(entriesPerCard) || 1);
  const perPage =
    perCard
    * Math.max(1, Math.floor(columns) || 1)
    * Math.max(1, Math.floor(rows) || 1);
  const pageCount = Math.max(1, Math.ceil(list.length / perPage));

  return Array.from({ length: pageCount }, (unusedPage, page) =>
    list.slice(page * perPage, page * perPage + perPage)
  );
};

// How the live display splits its records across cards.
//
// A card holds up to the template's entry count, so chunking by that alone
// collapses a short list into ONE card and the chosen column count is never
// visible — three columns of yahrzeits with three records due would render as a
// single card. The row is instead spread over as many cards as the layout asks
// for (never more than one record per card), and the records are balanced
// across them so a partially filled row reads evenly, the way the editor's
// preview does. Order is preserved: card N always follows card N-1.
export const buildYurtzeitenDisplayCardSizes_N = (
  total: number,
  entriesPerCard: number,
  columns: number
): number[] => {
  const perCard = Math.max(1, Math.floor(entriesPerCard) || 1);
  const records = Math.max(0, Math.floor(total) || 0);
  if (!records) return [];

  const wanted = Math.min(Math.max(1, Math.floor(columns) || 1), records);
  const packedCount = Math.ceil(records / perCard);

  // Enough records to reach the chosen column count: fill each card to the
  // template's capacity, exactly as the editor preview slices them.
  if (packedCount >= wanted) {
    return Array.from({ length: packedCount }, (unused, index) =>
      Math.min(perCard, records - index * perCard)
    );
  }

  // Too few to fill even one row. Spread them over the columns instead of
  // collapsing into a single card, balanced so the row reads evenly.
  const baseSize = Math.floor(records / wanted);
  const remainder = records % wanted;

  return Array.from(
    { length: wanted },
    (unused, index) => baseSize + (index < remainder ? 1 : 0)
  );
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export const isTransparentYurtzeitenColor_N = (value: unknown): boolean =>
  String(value ?? '').trim().toLowerCase() === YURTZEITEN_TRANSPARENT_COLOR_N;

// Anything that is not a hex colour or the transparent keyword falls back —
// the field is free text, so a half-typed value must never reach the canvas as
// an invalid style. The fallback is the caller's default, so a stored value the
// app can't read renders as the built-in colour rather than as nothing.
export const normalizeYurtzeitenColor_N = (value: unknown, fallback: string): string => {
  const color = String(value ?? '').trim();
  if (!color) return fallback;
  if (isTransparentYurtzeitenColor_N(color)) return YURTZEITEN_TRANSPARENT_COLOR_N;
  return HEX_COLOR_PATTERN.test(color) ? color : fallback;
};

// The tokens the renderer swaps for a record's own values.
//
// The English name is offered three ways: whole, and as each of the two halves
// the record stores it in. A design that wants the given and family names on
// separate lines, or one of them styled differently, uses {firstName} /
// {lastName}; {englishName} is the two of them joined, which is what the
// shipped layouts print.
export const YURTZEITEN_DESIGN_VARIABLES_N = [
  { value: 'englishName', label: 'English Name' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'hebrewName', label: 'Hebrew Name' },
  { value: 'gregorianDate', label: 'English Date' },
  { value: 'hebrewDate', label: 'Hebrew Date' },
  { value: 'comment', label: 'Comment' },
];

// Marks one record's block, and the <img> standing in for the record's photo.
export const YURTZEITEN_ENTRY_ATTR = 'data-yz-layout';
export const YURTZEITEN_ENTRY_VALUE = 'entry';
export const YURTZEITEN_PHOTO_IMAGE_ATTR = 'data-yz-photo';
// Marks a standalone record-photo row that deliberately reaches through the
// card padding to both inner border edges. Kept separate from the photo marker
// because Side by Side photos remain inside their own authored column.
export const YURTZEITEN_PHOTO_FULL_WIDTH_ATTR = 'data-yz-photo-full';

// Marks the block a record's comment is written into. Put on at render time,
// before the {comment} token is replaced, so the stylesheet can stop a long note
// from deciding how wide the card is — see `[data-yz-comment]` in
// assets/style/yurtzeiten_design_canvas.css.
export const YURTZEITEN_COMMENT_BLOCK_ATTR = 'data-yz-comment';

// The window a long note is run past when the design does not fit its artboard,
// and the two pieces inside it: the track that travels, and one copy of the note
// per lap. Put on at render time around the block marked above — see
// `wrapCommentBlocksForScroll` in utils/yurtzeiten_design_render.ts, the
// measurements in components/yurtzeiten_N/Yurtzeiten_Template_Render.vue, and
// `[data-yz-comment-viewport]` in assets/style/yurtzeiten_design_canvas.css.
export const YURTZEITEN_COMMENT_VIEWPORT_ATTR = 'data-yz-comment-viewport';
export const YURTZEITEN_COMMENT_TRACK_ATTR = 'data-yz-comment-track';
export const YURTZEITEN_COMMENT_COPY_ATTR = 'data-yz-comment-copy';

// Added to the viewport once a window has been measured for it. Until then the
// wrapper is inert and the note renders exactly as it always did.
export const YURTZEITEN_COMMENT_SCROLLING_CLASS = 'yz-comment-scrolling';

// A neutral 4:3 placeholder, inline so it needs no network round trip and can
// never 404. Shown until the design has a record photo or an uploaded image.
export const YURTZEITEN_PHOTO_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="168" height="122">' +
      '<rect width="168" height="122" fill="#e8edf5"/>' +
      '<path d="M56 84l20-25 15 19 12-14 21 27z" fill="#c3cede"/>' +
      '<circle cx="60" cy="45" r="10" fill="#c3cede"/>' +
    '</svg>'
  );

const photo = (width: number) =>
  `<p style="text-align: center"><img src="${YURTZEITEN_PHOTO_PLACEHOLDER}" ${YURTZEITEN_PHOTO_IMAGE_ATTR}="true" ${YURTZEITEN_PHOTO_FULL_WIDTH_ATTR}="true" width="${width}"></p>`;

// One record's block: the English name over the Hebrew name, exactly as the
// original fixed layouts drew them.
const entry = (englishSize: number, hebrewSize: number, align = 'center') =>
  `<div ${YURTZEITEN_ENTRY_ATTR}="${YURTZEITEN_ENTRY_VALUE}">` +
    `<p style="text-align: ${align}"><span style="font-family: FbFrank; font-size: ${englishSize}px">{englishName}</span></p>` +
    `<p dir="rtl" style="text-align: ${align}"><span style="font-family: TzlilimPM; font-size: ${hebrewSize}px">{hebrewName}</span></p>` +
  `</div>`;

const repeat = (count: number, build: () => string) =>
  Array.from({ length: count }, build).join('');

// The three layouts, in the order they are shown on the Design tab. Each ships
// the full design — the editor opens on exactly what the card previews.
export const YURTZEITEN_DESIGN_TEMPLATES_N: YurtzeitenDesignTemplate_N[] = [
  {
    id: 'stacked',
    label: 'Stacked',
    description: 'Portrait photo above a centred column of names.',
    entryCount: 3,
    defaults: {
      html: photo(168) + repeat(3, () => entry(18, 22)),
    },
  },
  {
    id: 'two-column',
    label: 'Two Column',
    description: 'Photo above two columns of names — fits twice as many.',
    entryCount: 4,
    defaults: {
      html:
        photo(148) +
        `<div ${YURTZEITEN_ENTRY_ATTR}="columns">${repeat(4, () => entry(16, 20))}</div>`,
    },
  },
  {
    id: 'side-by-side',
    label: 'Side by Side',
    description: 'Photo on the left, names stacked beside it.',
    entryCount: 2,
    defaults: {
      html:
        `<div ${YURTZEITEN_ENTRY_ATTR}="side">` +
          `<div ${YURTZEITEN_ENTRY_ATTR}="photo-col">` +
            `<p><img src="${YURTZEITEN_PHOTO_PLACEHOLDER}" ${YURTZEITEN_PHOTO_IMAGE_ATTR}="true" width="148"></p>` +
          `</div>` +
          `<div ${YURTZEITEN_ENTRY_ATTR}="names-col">${repeat(2, () => entry(20, 24, 'left'))}</div>` +
        `</div>`,
    },
  },
];

export const DEFAULT_YURTZEITEN_TEMPLATE_ID_N = YURTZEITEN_DESIGN_TEMPLATES_N[0].id;

export const findYurtzeitenTemplate_N = (
  templateId: string
): YurtzeitenDesignTemplate_N =>
  YURTZEITEN_DESIGN_TEMPLATES_N.find((template) => template.id === templateId) ||
  YURTZEITEN_DESIGN_TEMPLATES_N[0];

// A fresh copy every call — the editor mutates what it is handed, so sharing
// the constant would let one template's edits leak into another's defaults.
export const defaultYurtzeitenTemplateSettings_N = (
  templateId: string
): YurtzeitenDesignTemplateSettings_N => ({
  html: findYurtzeitenTemplate_N(templateId).defaults.html,
  borderColor: YURTZEITEN_CARD_BORDER_COLOR_DEFAULT_N,
  backgroundColor: YURTZEITEN_CARD_BACKGROUND_COLOR_DEFAULT_N,
  columns: 1,
  rows: 1,
  cardPadding: YURTZEITEN_CARD_PADDING_DEFAULT_N,
  spacing: YURTZEITEN_CARD_SPACING_DEFAULT_N,
});

// Merge a saved settings map over the built-in defaults so every template
// always has a complete design, whatever the server had stored (a template
// added after the row was written, or an older payload from before the editor
// replaced the per-role style fields).
export const normalizeYurtzeitenDesignSettings_N = (
  saved: Record<string, any> | null | undefined
): Record<string, YurtzeitenDesignTemplateSettings_N> => {
  const source = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
  const settings: Record<string, YurtzeitenDesignTemplateSettings_N> = {};

  YURTZEITEN_DESIGN_TEMPLATES_N.forEach((template) => {
    const base = defaultYurtzeitenTemplateSettings_N(template.id);
    const stored = source[template.id];

    // Anything without usable html — including a row written against the old
    // { english, hebrew } shape — falls back to the template's starting design
    // rather than opening the editor empty.
    const html =
      stored && typeof stored === 'object' && typeof stored.html === 'string' && stored.html.trim()
        ? stored.html
        : base.html;
    const contentWidth = normalizeYurtzeitenContentWidth_N(stored?.contentWidth);

    // Rows written before the card colours existed carry neither field, so both
    // land on the defaults — which are the colours those designs were already
    // being drawn with.
    settings[template.id] = {
      html,
      borderColor: normalizeYurtzeitenColor_N(stored?.borderColor, base.borderColor),
      backgroundColor: normalizeYurtzeitenColor_N(stored?.backgroundColor, base.backgroundColor),
      columns: normalizeYurtzeitenCardColumns_N(stored?.columns),
      rows: normalizeYurtzeitenCardRows_N(stored?.rows),
      cardPadding: normalizeYurtzeitenCardPadding_N(stored?.cardPadding ?? base.cardPadding),
      spacing: normalizeYurtzeitenCardSpacing_N(stored?.spacing ?? base.spacing),
      ...(contentWidth ? { contentWidth } : {}),
    };
  });

  return settings;
};


// The template and complete settings a stored design row resolves to. Stale or
// unknown ids fall back to the first template, and missing fields to its
// defaults, so every caller reads the same design the renderer will draw.
export const resolveYurtzeitenDesign_N = (
  design: { templateId?: string | null; settings?: Record<string, any> | null } | null | undefined
): { template: YurtzeitenDesignTemplate_N; settings: YurtzeitenDesignTemplateSettings_N } => {
  const template = findYurtzeitenTemplate_N(
    design?.templateId || DEFAULT_YURTZEITEN_TEMPLATE_ID_N
  );
  const settings =
    normalizeYurtzeitenDesignSettings_N(design?.settings)[template.id]
    || defaultYurtzeitenTemplateSettings_N(template.id);
  return { template, settings };
};
